import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Calculator,
  Users,
  Upload,
  Download,
  Settings,
  FileText,
  ShoppingCart,
  AlertTriangle,
  Package,
  List,
  PlusCircle,
  Shield,
  CreditCard,
  LogOut,
  Home,
  ChevronLeft
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PERMISSIONS } from '../types';
import Logo from './Logo';

const navigation = [
  {
    name: 'Accueil',
    href: '/dashboard',
    icon: Home,
    permission: null,
  },
  {
    name: 'Gestion des Prix',
    permission: PERMISSIONS.PRICE_MANAGEMENT,
    items: [
      { name: 'Gestion de Prix', href: '/prices', icon: Calculator, permission: PERMISSIONS.PRICE_MANAGEMENT },
      { name: 'Liste des Prix', href: '/prices/list', icon: FileText, permission: PERMISSIONS.PRICE_LIST },
      { name: 'Liste des Prix Boutique', href: '/prices/shop', icon: ShoppingCart, permission: PERMISSIONS.SHOP_PRICE_LIST },
    ]
  },
  {
    name: 'Gestion des Pièces',
    permission: PERMISSIONS.PARTS_MANAGEMENT,
    items: [
      { name: 'Gestion Des pièces sur commande', href: '/parts/management', icon: Package, permission: PERMISSIONS.PARTS_MANAGEMENT },
      { name: 'Liste des pièces sur commande', href: '/parts/list', icon: List, permission: PERMISSIONS.PARTS_LIST },
      { name: 'Nouvelle Commande', href: '/orders/new', icon: PlusCircle, permission: PERMISSIONS.NEW_ORDER },
    ]
  },
  {
    name: 'Litiges',
    permission: PERMISSIONS.NEW_DISPUTE,
    items: [
      { name: 'Nouveau Litige', href: '/disputes/new', icon: AlertTriangle, permission: PERMISSIONS.NEW_DISPUTE },
      { name: 'Gestion Litige Assurance', href: '/disputes/insurance', icon: Shield, permission: PERMISSIONS.INSURANCE_DISPUTE },
      { name: 'Gestion Litige', href: '/disputes/management', icon: AlertTriangle, permission: PERMISSIONS.DISPUTE_MANAGEMENT },
    ]
  },
  {
    name: 'Finances',
    permission: PERMISSIONS.PAYMENT_TRACKING,
    items: [
      { name: 'Suivi Paiement', href: '/payments', icon: CreditCard, permission: PERMISSIONS.PAYMENT_TRACKING },
    ]
  },
  {
    name: 'Administration',
    adminOnly: true,
    items: [
      { name: 'Configuration', href: '/configuration', icon: Settings, adminOnly: true },
    ]
  },
];

export default function MainNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  if (!user) {
    return null;
  }

  const isAdmin = user.email === 'info@allo-reparateur.tn';
  const showBackButton = location.pathname !== '/dashboard';

  const hasPermission = (permission: string | null) => {
    if (!permission) return true;
    if (isAdmin) return true;
    return user.permissions.includes(permission);
  };

  const filteredNavigation = navigation.filter(section => {
    if (section.adminOnly) {
      return isAdmin;
    }
    if (section.permission) {
      return hasPermission(section.permission);
    }
    return true;
  });

  const handleNavigate = (href: string, permission: string | null) => {
    if (hasPermission(permission)) {
      navigate(href);
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <nav className="bg-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            {showBackButton && (
              <button
                onClick={() => navigate(-1)}
                className="mr-4 p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
              >
                <ChevronLeft className="h-6 w-6 text-gray-600" />
              </button>
            )}
            <Logo className="mr-8" size="small" showText={false} />
            {filteredNavigation.map((section) => {
              if (!section.items) {
                const Icon = section.icon;
                return (
                  <button
                    key={section.name}
                    onClick={() => handleNavigate(section.href, section.permission)}
                    className={`h-16 inline-flex items-center px-4 text-gray-700 hover:text-gray-900 hover:bg-gray-50 font-medium ${
                      location.pathname === section.href ? 'text-primary border-b-2 border-primary' : ''
                    }`}
                  >
                    <Icon className="h-5 w-5 mr-2" />
                    {section.name}
                  </button>
                );
              }

              const filteredItems = section.items.filter(item => {
                if (item.adminOnly) {
                  return isAdmin;
                }
                if (item.permission) {
                  return hasPermission(item.permission);
                }
                return true;
              });

              if (filteredItems.length === 0) return null;

              return (
                <div key={section.name} className="relative group inline-block text-left">
                  <div>
                    <button className={`h-16 inline-flex items-center px-4 text-gray-700 hover:text-gray-900 hover:bg-gray-50 font-medium ${
                      filteredItems.some(item => location.pathname.startsWith(item.href)) ? 'text-primary border-b-2 border-primary' : ''
                    }`}>
                      {section.name}
                    </button>
                  </div>
                  <div className="origin-top-left absolute left-0 mt-0 w-64 rounded-b-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none hidden group-hover:block z-50">
                    <div className="py-1">
                      {filteredItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.href;
                        return (
                          <button
                            key={item.name}
                            onClick={() => handleNavigate(item.href, item.permission)}
                            className={`${
                              isActive
                                ? 'bg-gray-100 text-gray-900'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                            } group flex items-center w-full px-4 py-3 text-sm text-left`}
                          >
                            <Icon className="mr-3 h-5 w-5" />
                            {item.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center">
            <div className="mr-4 text-sm text-gray-700">
              {user.first_name} {user.last_name}
            </div>
            <button
              onClick={signOut}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              <LogOut className="h-5 w-5 mr-2" />
              Déconnexion
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}